import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import API_BASE_URL from '../config/api';
import './DashboardPage.css';

import DashboardHome from './dashboard/DashboardHome';
import ProjectsView from './dashboard/ProjectsView';
import ProjectDetailView from './dashboard/ProjectDetailView';
import AdminAccessView from './dashboard/AdminAccessView';
import {
  dashboardDocuments,
  dashboardMessages,
  dashboardProjects,
  dashboardTasks,
} from './dashboard/dashboardData';

export default function Dashboard() {
  const [checkingToken, setCheckingToken] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [activeMenu, setActiveMenu] = useState('home');
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const token = localStorage.getItem('authToken');
  const username = localStorage.getItem('username');
  const role = localStorage.getItem('role') || 'guest';
  const is2FAVerified = localStorage.getItem('is2FAVerified');
  const dashboardMenuItems = [
    { key: 'home', label: 'Dashboard Home' },
    { key: 'projects', label: 'Projects' },
    ...(role === 'admin' ? [{ key: 'access', label: 'User Access' }] : []),
  ];

  useEffect(() => {
    const verifyToken = async () => {
      const currentToken = localStorage.getItem('authToken');

      if (!currentToken) {
        setIsTokenValid(false);
        setCheckingToken(false);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/verify-token`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        });

        const data = await response.json();

        if (response.ok && data.status === 'success') {
          localStorage.setItem('role', data.role || 'guest');
          setIsTokenValid(true);
        } else {
          throw new Error(data.message || 'Token verification failed');
        }
      } catch (error) {
        console.error('Token verification error:', error);

        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        localStorage.removeItem('role');
        localStorage.removeItem('is2FAVerified');

        setIsTokenValid(false);
      } finally {
        setCheckingToken(false);
      }
    };

    verifyToken();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('is2FAVerified');
    window.location.replace('/login');
  };

  if (checkingToken) {
    return <div className="dashboard-loading">Checking access...</div>;
  }

  if (!token || !isTokenValid) {
    return <Navigate to="/login" replace />;
  }

  if (is2FAVerified !== 'true') {
    return <Navigate to="/verify" replace />;
  }

  const selectedProject =
    selectedProjectId != null
      ? dashboardProjects.find((project) => project.id === selectedProjectId)
      : null;

  const renderContent = () => {
    if (activeMenu === 'home') {
      return (
        <DashboardHome
          username={username}
          role={role}
          projects={dashboardProjects}
          documents={dashboardDocuments}
          tasks={dashboardTasks}
          messages={dashboardMessages}
        />
      );
    }

    if (activeMenu === 'projects') {
      if (selectedProject) {
        return (
          <ProjectDetailView
            project={selectedProject}
            onBack={() => setSelectedProjectId(null)}
          />
        );
      }

      return (
        <ProjectsView
          projects={dashboardProjects}
          onSelectProject={(projectId) => setSelectedProjectId(projectId)}
        />
      );
    }

    if (activeMenu === 'access' && role === 'admin') {
      return <AdminAccessView />;
    }

    return null;
  };

  return (
    <div className="dashboard-page">
      <aside className="dashboard-sidebar">
        <div>
          <div className="dashboard-logo">LEEJIN</div>
          <div className="dashboard-sidebar-subtitle">Client Workspace</div>

          <nav className="dashboard-nav">
            {dashboardMenuItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`dashboard-nav-item ${activeMenu === item.key ? 'active' : ''}`}
                onClick={() => {
                  setActiveMenu(item.key);
                  if (item.key !== 'projects') {
                    setSelectedProjectId(null);
                  }
                }}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        <button type="button" className="dashboard-logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </aside>

      <main className="dashboard-main">{renderContent()}</main>
    </div>
  );
}
